import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { saltearOnboarding } from '../lib/devSkipOnboarding';
import { borradorQueAtrapa } from '../lib/onboardingMarca/marcaNueva';

/**
 * Los dos comportamientos que `DashboardLayout` hacía además de dibujar el chrome —
 * ADR-2026-07-31-shell-nuevo-urls-viejas.
 *
 * Al montar el shell nuevo en desktop, esas rutas dejaron de pasar por
 * `DashboardLayout`. El chrome era justamente lo que se quería cambiar; **esto** no,
 * y se habría ido en silencio: el guard de marca en borrador es lo que evita que una
 * cuenta con el alta a medias entre a una app donde todas las queries filtran
 * `is_draft = false` y no devuelven nada. Un usuario en ese estado veía la home vacía
 * sin explicación en vez de volver a terminar su marca.
 *
 * Vive en un hook y no duplicado en los dos layouts para que no puedan divergir: si
 * mañana el guard cambia, cambia para los dos shells a la vez.
 */
export function useShellGuards() {
  const navigate = useNavigate();
  const location = useLocation();

  // Guard: si el usuario tiene una marca en draft (onboarding interrumpido),
  // redirigir al editor para que termine.
  // Bypass dev (ADR-2026-07-30-bypass-dev-onboarding-entrar-app): con el flag prendido no se rebota al
  // editor, así una cuenta con el alta abandonada a medias sigue siendo usable en local.
  useEffect(() => {
    if (saltearOnboarding()) return;
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .maybeSingle();
        const idCompany = (profile as any)?.id_company as string | undefined;
        if (!idCompany || cancelled) return;
        const { data: drafts } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', idCompany)
          .eq('is_draft', true)
          .order('created_at', { ascending: false })
          .limit(1);
        if (cancelled) return;
        const draftId = (drafts && drafts[0]?.id) as string | undefined;
        if (!draftId) return;
        // Con facturación por marca, una marca nueva en borrador está esperando su pago: no
        // atrapa al dueño (y si ya está pagada, se activa). Sin la bandera devuelve el mismo
        // borrador (ADR-2026-10-28-marca-nueva-con-pago).
        const destino = await borradorQueAtrapa(idCompany, draftId);
        if (cancelled) return;
        if (destino && !window.location.pathname.startsWith('/onboarding/revisar-marca')) {
          navigate(`/onboarding/revisar-marca/${destino}`, { replace: true });
        }
      } catch {
        // silencioso
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Que ningún overlay/modal deje el scroll global trabado al cerrarse.
  useEffect(() => {
    try {
      document.body.style.overflow = 'auto';
      (document.documentElement as HTMLElement).style.overflow = 'auto';
    } catch {
      // noop
    }
  }, [location.pathname]);
}

export default useShellGuards;
